/**
 * VISTOERA_AI_COST_PRICING_PROVIDER_COST_POINTS_REVENUE_MARGIN_CONTROL_
 * PLANE_P0_V1 — Create / Edit Drawer for the "AI 成本与定价" page.
 *
 * Hard rule (task spec §23): Provider / Model / Execution Option /
 * Credential fields are Select-from-existing-canonical only. This Drawer
 * never creates a ProviderModel / ProviderOption / ProviderCredential —
 * if one is missing, the Admin goes to 模型管理 / 功能节点管理 /
 * Credential 中心 first.
 */

import {
  Alert,
  Button,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from 'antd'
import { useEffect, useMemo, useState } from 'react'
import {
  platformCreateAiPricingEntry,
  platformListProviderCapabilities,
  platformListProviderCredentials,
  platformListProviderModels,
  platformListProviderOptions,
  platformUpdateAiPricingEntry,
  type ProviderCapableNodeRow,
  type ProviderOptionAdminRow,
} from '../api'
import type {
  AiPricingEntryRow,
  AiPricingReferenceConfig,
  ProviderCredentialRow,
  ProviderModelRow,
} from '../types'
import {
  computeFinancials,
  financialStatusLabelZh,
  formatMarginPercent,
  formatMoneyAmount,
  marginTone,
} from './aiPricingCalculations'
import { platformColors } from './theme'

const BILLING_UNIT_OPTIONS = [
  { value: 'per_image', label: '张 (per_image)' },
  { value: 'per_video', label: '个视频 (per_video)' },
  { value: 'per_second', label: '秒 (per_second)' },
  { value: 'per_generated_second', label: '秒 (per_generated_second)' },
  { value: 'per_success', label: '次 (per_success)' },
  { value: 'per_1k_tokens', label: '1K tokens (per_1k_tokens)' },
  { value: 'per_token', label: 'token (per_token)' },
  { value: 'per_worker_minute', label: 'Worker 分钟 (per_worker_minute)' },
  { value: 'per_media_minute', label: '媒体分钟 (per_media_minute)' },
  { value: 'per_1k_characters', label: '1K 字符 (per_1k_characters)' },
]

const CURRENCY_OPTIONS = [
  { value: 'CNY', label: 'CNY (¥)' },
  { value: 'USD', label: 'USD ($)' },
]

const CAPABILITY_DEFAULTS: Record<string, { category_key: string; operation_key: string; billing_unit: string }> = {
  'image.generation': {
    category_key: 'image',
    operation_key: 'image.generate.text_to_image',
    billing_unit: 'per_image',
  },
  'video.generation': {
    category_key: 'video',
    operation_key: 'video.generate.text_to_video',
    billing_unit: 'per_generated_second',
  },
  'tryon.clothing': {
    category_key: 'image',
    operation_key: 'tryon.clothing',
    billing_unit: 'per_image',
  },
}

type FormValues = {
  capability?: string
  provider_option_id?: string | null
  provider_model_id?: string | null
  provider_credential_id?: string | null
  category_key?: string
  display_name?: string
  operation_key?: string
  provider_key?: string
  model_key?: string
  billing_unit?: string
  points_per_unit?: number | null
  estimated_cost_per_unit?: number | null
  cost_currency?: string | null
  enabled?: boolean
  pricing_status?: string
  notes?: string | null
}

export default function AIPricingEntryDrawer({
  open,
  mode,
  entry,
  referenceConfig,
  onClose,
  onSaved,
}: {
  open: boolean
  mode: 'create' | 'edit'
  entry: AiPricingEntryRow | null
  referenceConfig: AiPricingReferenceConfig | null
  onClose: () => void
  onSaved: (row: AiPricingEntryRow) => void
}) {
  const [form] = Form.useForm<FormValues>()
  const [saving, setSaving] = useState(false)
  const [nodes, setNodes] = useState<ProviderCapableNodeRow[]>([])
  const [options, setOptions] = useState<ProviderOptionAdminRow[]>([])
  const [models, setModels] = useState<ProviderModelRow[]>([])
  const [credentials, setCredentials] = useState<ProviderCredentialRow[]>([])
  const [loadingMeta, setLoadingMeta] = useState(false)

  const watched = Form.useWatch([], form)

  useEffect(() => {
    if (!open) return
    setLoadingMeta(true)
    Promise.all([
      platformListProviderCapabilities().then((r) => r.data.nodes),
      platformListProviderModels({ limit: 500 }).then((r) => r.data.items ?? []),
      platformListProviderCredentials().then((r) => r.data.credentials ?? []),
    ])
      .then(([n, m, c]) => {
        setNodes(n)
        setModels(m)
        setCredentials(c)
      })
      .catch((err) => message.error((err as Error).message))
      .finally(() => setLoadingMeta(false))
  }, [open])

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && entry) {
      form.setFieldsValue({
        provider_option_id: entry.execution_option_id,
        provider_model_id: null,
        provider_credential_id: entry.provider_credential_id,
        category_key: entry.category_key,
        display_name: entry.display_name,
        operation_key: entry.operation_key,
        provider_key: entry.provider_key,
        model_key: entry.model_key,
        billing_unit: entry.billing_unit,
        points_per_unit: entry.points_per_unit != null ? Number(entry.points_per_unit) : null,
        estimated_cost_per_unit:
          entry.supplier_cost_per_unit != null ? Number(entry.supplier_cost_per_unit) : null,
        cost_currency: entry.cost_currency ?? 'CNY',
        enabled: entry.enabled,
        pricing_status: entry.pricing_status,
        notes: null,
      })
    } else {
      form.resetFields()
      form.setFieldsValue({
        cost_currency: 'CNY',
        enabled: false,
        pricing_status: 'PRICING_REQUIRED',
        billing_unit: 'per_image',
        category_key: 'image',
      })
    }
  }, [open, mode, entry, form])

  const loadOptionsForCapability = async (capability: string) => {
    try {
      const res = await platformListProviderOptions(capability)
      setOptions(res.data.options)
    } catch (err) {
      message.error((err as Error).message)
      setOptions([])
    }
  }

  const onCapabilityChange = (capability: string) => {
    form.setFieldValue('capability', capability)
    form.setFieldValue('provider_option_id', undefined)
    const defaults = CAPABILITY_DEFAULTS[capability]
    if (defaults) {
      form.setFieldsValue(defaults)
    }
    void loadOptionsForCapability(capability)
  }

  const onOptionChange = (optionId: string) => {
    const opt = options.find((o) => o.id === optionId)
    if (!opt) return
    form.setFieldValue('provider_option_id', optionId)
    form.setFieldValue('display_name', opt.frontend_display_name)
    if (opt.mapping?.provider_key) {
      form.setFieldValue('provider_key', opt.mapping.provider_key)
    }
    if (opt.mapping?.model_external_id) {
      form.setFieldValue('model_key', opt.mapping.model_external_id)
    }
    if (opt.mapping?.provider_model_id) {
      form.setFieldValue('provider_model_id', opt.mapping.provider_model_id)
    }
  }

  const onModelChange = (modelId: string) => {
    const model = models.find((m) => m.id === modelId)
    if (!model) return
    form.setFieldValue('provider_model_id', modelId)
    form.setFieldValue('model_key', model.provider_model_id)
    if (model.provider_key) form.setFieldValue('provider_key', model.provider_key)
  }

  const preview = useMemo(() => {
    return computeFinancials({
      pointsPerUnit: watched?.points_per_unit ?? null,
      estimatedCostPerUnit: watched?.estimated_cost_per_unit ?? null,
      costCurrency: watched?.cost_currency ?? null,
      referencePointValue: referenceConfig?.reference_point_value ?? null,
      referenceCurrency: referenceConfig?.reference_currency ?? null,
    })
  }, [watched, referenceConfig])

  const toneColor = (tone: ReturnType<typeof marginTone>) => {
    switch (tone) {
      case 'green':
        return platformColors.success
      case 'orange':
        return platformColors.warning
      case 'yellow':
        return '#CA8A04'
      case 'red':
        return platformColors.danger
      default:
        return platformColors.secondaryText
    }
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)
      if (mode === 'create') {
        if (!values.category_key || !values.display_name || !values.operation_key || !values.provider_key || !values.model_key || !values.billing_unit) {
          message.error('请完整填写计费身份字段')
          return
        }
        const res = await platformCreateAiPricingEntry({
          category_key: values.category_key,
          display_name: values.display_name,
          operation_key: values.operation_key,
          provider_key: values.provider_key,
          model_key: values.model_key,
          billing_unit: values.billing_unit,
          provider_option_id: values.provider_option_id || null,
          provider_model_id: values.provider_model_id || null,
          provider_credential_id: values.provider_credential_id || null,
          points_per_unit: values.points_per_unit ?? null,
          estimated_cost_per_unit: values.estimated_cost_per_unit ?? null,
          cost_currency: values.cost_currency ?? null,
          enabled: values.enabled ?? false,
          pricing_status: values.pricing_status ?? 'PRICING_REQUIRED',
          notes: values.notes ?? null,
        })
        message.success('定价已创建')
        onSaved(res.data)
      } else if (entry) {
        const res = await platformUpdateAiPricingEntry(entry.id, {
          provider_option_id: values.provider_option_id ?? null,
          provider_model_id: values.provider_model_id ?? null,
          provider_credential_id: values.provider_credential_id ?? null,
          points_per_unit: values.points_per_unit ?? null,
          estimated_cost_per_unit: values.estimated_cost_per_unit ?? null,
          cost_currency: values.cost_currency ?? null,
          enabled: values.enabled,
          pricing_status: values.pricing_status,
          display_name: values.display_name,
          notes: values.notes ?? null,
        })
        message.success('定价已更新（仅影响未来计费）')
        onSaved(res.data)
      }
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) return
      message.error((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Drawer
      title={mode === 'create' ? '新增定价' : '编辑定价'}
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={saving} onClick={() => void handleSave()}>
            保存
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="修改仅影响未来产生的计费，历史计费快照不会追溯变化。"
      />
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Provider / Model / 执行方案只能选择已有实体。如缺失，请先到模型管理或功能节点管理中创建。"
      />

      <Form form={form} layout="vertical" disabled={loadingMeta}>
        {mode === 'create' && (
          <Form.Item name="capability" label="Function Node">
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="选择功能节点（用于加载执行方案）"
              options={nodes.map((n) => ({
                value: n.capability,
                label: n.fallback_label_zh,
              }))}
              onChange={onCapabilityChange}
            />
          </Form.Item>
        )}

        <Form.Item name="provider_option_id" label="Execution Option">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={mode === 'create' ? '先选 Function Node，再选已有执行方案' : '关联已有执行方案（可选）'}
            options={
              mode === 'create'
                ? options.map((o) => ({ value: o.id, label: o.frontend_display_name }))
                : entry?.execution_option_id
                  ? [{ value: entry.execution_option_id, label: entry.execution_option_label }]
                  : options.map((o) => ({ value: o.id, label: o.frontend_display_name }))
            }
            onChange={(v) => v && onOptionChange(v)}
          />
        </Form.Item>

        <Form.Item name="provider_model_id" label="Provider Model">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="选择已有模型（可选）"
            options={models.map((m) => ({
              value: m.id,
              label: `${m.provider_display_name ?? m.provider_key ?? '?'} / ${m.display_name_override || m.display_name}`,
            }))}
            onChange={(v) => v && onModelChange(v)}
          />
        </Form.Item>

        <Form.Item name="provider_credential_id" label="Provider Account / Credential Profile">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="平台默认（不区分账号）"
            options={credentials.map((c) => ({
              value: c.id,
              label: c.account_label || c.name,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="display_name"
          label="显示名称"
          rules={mode === 'create' ? [{ required: true, message: '必填' }] : undefined}
        >
          <Input />
        </Form.Item>

        {mode === 'create' && (
          <>
            <Form.Item name="operation_key" label="Operation Key" rules={[{ required: true, message: '必填' }]}>
              <Input placeholder="e.g. image.generate.text_to_image" />
            </Form.Item>
            <Form.Item name="provider_key" label="Provider Key" rules={[{ required: true, message: '必填' }]}>
              <Input placeholder="e.g. bfl / volcengine_ark / local" />
            </Form.Item>
            <Form.Item name="model_key" label="Model Key" rules={[{ required: true, message: '必填' }]}>
              <Input placeholder="e.g. flux-2-pro" />
            </Form.Item>
            <Form.Item name="category_key" label="Category" rules={[{ required: true, message: '必填' }]}>
              <Select
                options={[
                  { value: 'image', label: 'image' },
                  { value: 'video', label: 'video' },
                  { value: 'text', label: 'text' },
                  { value: 'audio', label: 'audio' },
                  { value: 'video_planning', label: 'video_planning' },
                  { value: 'reference_media', label: 'reference_media' },
                  { value: 'language', label: 'language' },
                  { value: 'other', label: 'other' },
                ]}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="billing_unit"
          label="计费单位"
          rules={mode === 'create' ? [{ required: true, message: '必填' }] : undefined}
        >
          <Select options={BILLING_UNIT_OPTIONS} disabled={mode === 'edit'} />
        </Form.Item>

        <Divider />

        <Form.Item name="estimated_cost_per_unit" label="供应商成本">
          <InputNumber style={{ width: '100%' }} min={0} step={0.001} stringMode={false} />
        </Form.Item>
        <Form.Item name="cost_currency" label="Currency">
          <Select options={CURRENCY_OPTIONS} />
        </Form.Item>

        <Form.Item name="points_per_unit" label="积分消耗">
          <InputNumber style={{ width: '100%' }} min={0} step={1} />
        </Form.Item>

        <Divider />

        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          自动计算（与服务端公式一致）
        </Typography.Text>
        <div
          style={{
            background: platformColors.surface,
            border: `1px solid ${platformColors.border}`,
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <div>积分参考价值：{formatMoneyAmount(referenceConfig?.reference_point_value, referenceConfig?.reference_currency)} / point</div>
          <div>预计收入：{formatMoneyAmount(preview.revenuePerUnit, referenceConfig?.reference_currency)}</div>
          <div>
            预计毛利：
            <span style={{ color: preview.grossProfitPerUnit != null && preview.grossProfitPerUnit < 0 ? platformColors.danger : undefined }}>
              {formatMoneyAmount(preview.grossProfitPerUnit, referenceConfig?.reference_currency)}
            </span>
          </div>
          <div>
            预计毛利率：
            <span style={{ color: toneColor(marginTone(preview.grossMargin)), fontWeight: 600 }}>
              {preview.status === 'CURRENCY_MISMATCH'
                ? '待汇率'
                : preview.status !== 'OK' && preview.grossMargin == null
                  ? financialStatusLabelZh(preview.status)
                  : formatMarginPercent(preview.grossMargin)}
            </span>
          </div>
        </div>

        <Form.Item name="pricing_status" label="定价状态">
          <Select
            options={[
              { value: 'ACTIVE', label: 'ACTIVE' },
              { value: 'PRICING_REQUIRED', label: 'PRICING_REQUIRED' },
              { value: 'DRAFT', label: 'DRAFT' },
              { value: 'DEPRECATED', label: 'DEPRECATED' },
              { value: 'BLOCKED_BY_CONTRACT', label: 'BLOCKED_BY_CONTRACT' },
            ]}
          />
        </Form.Item>
        <Form.Item name="enabled" label="启用计费" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="notes" label="备注">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
